/**
 * Sticky Header Component
 * Handles sticky header behavior with scroll-based hiding/showing
 */

class StickyHeader extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.querySelector('.section-header');
    if (!this.header) return;
    this.announcementOffsetInitialized = false;
    
    // Find the parent group that might have absolute positioning
    this.headerGroup = this.header.closest('.shopify-section-group-header-group') || this.header;
    
    // Read sticky types from attributes
    this.desktopStickyType = this.getAttribute('data-sticky-type-desktop') || this.getAttribute('data-sticky-type') || 'none';
    this.mobileStickyType = this.getAttribute('data-sticky-type-mobile') || 'none';
    this.headerBounds = {};
    this.mobileMediaQuery = window.matchMedia('(max-width: 990px)');

    this.setHeaderHeight();
    this.setAnnouncementBarOffset();
    this.updateStickyType();

    this.mediaQueryHandler = () => {
      this.setHeaderHeight();
      this.setAnnouncementBarOffset();
      this.updateStickyType();
    };
    this.mobileMediaQuery.addEventListener('change', this.mediaQueryHandler);
    this.resizeHandler = () => this.setAnnouncementBarOffset();
    window.addEventListener('resize', this.resizeHandler);

    this.currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
    this.preventReveal = false;
    this.predictiveSearch = this.querySelector('predictive-search');

    this.onScrollHandler = this.onScroll.bind(this);
    this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

    this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.addEventListener('scroll', this.onScrollHandler, false);

    this.createObserver();

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        if (this.isConnected) this.setAnnouncementBarOffset();
      });
    }
  }

  applyInitialStickyState() {
    this.setAnnouncementBarOffset();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const isMobile = this.mobileMediaQuery.matches;
    const currentStickyType = isMobile ? this.mobileStickyType : this.desktopStickyType;
    const isIndexPage = document.body.classList.contains('index');
    const isBlogPage = document.body.classList.contains('blog');
    const isTransparentHeaderPage = isIndexPage || isBlogPage;

    if (scrollTop > this.headerBounds.bottom) {
      this.header.classList.add('scrolled-past-header');
      if (currentStickyType === 'on-scroll-up') {
        if (isTransparentHeaderPage && this.headerGroup) {
          this.headerGroup.classList.add('header-group-sticky-enabled');
          this.headerGroup.style.position = 'relative';
        }
        this.header.classList.remove('shopify-section-header-sticky');
      }
    } else {
      if (currentStickyType === 'on-scroll-up' && isTransparentHeaderPage && this.headerGroup) {
        this.headerGroup.classList.remove('header-group-sticky-enabled');
        this.headerGroup.style.position = '';
      }
      if (currentStickyType === 'on-scroll-up') {
        this.header.classList.remove('shopify-section-header-sticky');
      }
    }
  }

  updateStickyType() {
    if (!this.header) return;
    
    const isMobile = this.mobileMediaQuery.matches;
    const currentStickyType = isMobile ? this.mobileStickyType : this.desktopStickyType;
    this.syncAnnouncementStickyState(isMobile, currentStickyType);
    
    this.headerIsAlwaysSticky = currentStickyType === 'always';

    const wasScrolled = this.header.classList.contains('scrolled-past-header');

    this.header.classList.remove('shopify-section-header-sticky', 'shopify-section-header-hidden', 'scrolled-past-header', 'animate');
    if (this.headerGroup) {
      this.headerGroup.classList.remove('header-group-sticky-enabled');
    }

    if (currentStickyType === 'none') {
      return;
    } else if (this.headerIsAlwaysSticky) {
      this.header.classList.add('shopify-section-header-sticky');
      
      // On index/blog pages, only enable sticky positioning when scrolled past header
      const isIndexPage = document.body.classList.contains('index');
      const isBlogPage = document.body.classList.contains('blog');
      const isTransparentHeaderPage = isIndexPage || isBlogPage;
      if (this.headerGroup) {
        if (isTransparentHeaderPage && !wasScrolled) {
          // On index/blog page, keep absolute positioning until scrolled
          this.headerGroup.classList.remove('header-group-sticky-enabled');
          this.headerGroup.style.position = '';
        } else {
          // On other pages or when scrolled, use relative for sticky to work
          this.headerGroup.classList.add('header-group-sticky-enabled');
          this.headerGroup.style.position = 'relative';
        }
      }
      // Restore scrolled state if it was scrolled
      if (wasScrolled) {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        if (this.headerBounds && scrollTop > this.headerBounds.bottom) {
          this.header.classList.add('scrolled-past-header');
          // When scrolled on index/blog, enable sticky positioning
          if (isTransparentHeaderPage && this.headerGroup) {
            this.headerGroup.classList.add('header-group-sticky-enabled');
            this.headerGroup.style.position = 'relative';
          }
        }
      }
    } else if (currentStickyType === 'on-scroll-up') {
      const isIndexPage = document.body.classList.contains('index');
      const isBlogPage = document.body.classList.contains('blog');
      const isTransparentHeaderPage = isIndexPage || isBlogPage;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      if (this.headerGroup) {
        if (scrollTop <= this.headerBounds.top) {
          if (isTransparentHeaderPage) {
            this.headerGroup.classList.remove('header-group-sticky-enabled');
            this.headerGroup.style.position = '';
          }
        } else if (scrollTop > this.headerBounds.bottom) {
          if (isTransparentHeaderPage) {
            this.headerGroup.classList.remove('header-group-sticky-enabled');
            this.headerGroup.style.position = '';
          }
        }
      }
    }
  }

  setHeaderHeight() {
    document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
  }

  setAnnouncementBarOffset() {
    const announcementGrid = document.querySelector('.announcement-bar-section .utility-bar__grid');
    const announcementOffset = announcementGrid ? Math.round(announcementGrid.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--announcement-bar-offset', `${announcementOffset}px`);
    this.classList.toggle('has-announcement-bar', announcementOffset > 0);

    if (!this.announcementOffsetInitialized) {
      this.announcementOffsetInitialized = true;
      requestAnimationFrame(() => {
        document.body.classList.add('announcement-offset-ready');
      });
    }
  }

  syncAnnouncementStickyState(isMobile, stickyType) {
    const hasAnnouncementBar = document.querySelector('.announcement-bar-section') !== null;
    const shouldStickAnnouncement = hasAnnouncementBar && isMobile && stickyType !== 'none';
    document.body.classList.toggle('mobile-sticky-header-enabled', shouldStickAnnouncement);
  }

  disconnectedCallback() {
    this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.removeEventListener('scroll', this.onScrollHandler);
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
    if (this.mediaQueryHandler && this.mobileMediaQuery) {
      this.mobileMediaQuery.removeEventListener('change', this.mediaQueryHandler);
    }
  }

  createObserver() {
    let observer = new IntersectionObserver((entries, observer) => {
      this.headerBounds = entries[0].intersectionRect;
      observer.disconnect();
      this.applyInitialStickyState();
    });

    observer.observe(this.header);
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const isMobile = this.mobileMediaQuery.matches;
    const currentStickyType = isMobile ? this.mobileStickyType : this.desktopStickyType;
    this.syncAnnouncementStickyState(isMobile, currentStickyType);

    if (this.predictiveSearch && this.predictiveSearch.isOpen) return;
    
    if (currentStickyType === 'none') {
      this.header.classList.remove('scrolled-past-header', 'shopify-section-header-sticky', 'shopify-section-header-hidden', 'animate');
      return;
    }

    this.headerIsAlwaysSticky = currentStickyType === 'always';

    const isIndexPage = document.body.classList.contains('index');
    const isBlogPage = document.body.classList.contains('blog');
    const isTransparentHeaderPage = isIndexPage || isBlogPage;
    
    // Handle 'on-scroll-up' behavior - toggle sticky class based on scroll direction
    if (currentStickyType === 'on-scroll-up') {
      if (scrollTop <= this.headerBounds.top) {
        // At top - remove sticky (behave like 'none')
        this.header.classList.remove('shopify-section-header-sticky', 'scrolled-past-header');
        this.header.classList.remove('bottom-border');
        if (isTransparentHeaderPage && this.headerGroup) {
          this.headerGroup.classList.remove('header-group-sticky-enabled');
          this.headerGroup.style.position = '';
        }
        requestAnimationFrame(this.reset.bind(this));
      } else if (scrollTop > this.headerBounds.bottom) {
        // Scrolled past header
        const scrollingDown = scrollTop > this.currentScrollTop;
        const scrollingUp = scrollTop < this.currentScrollTop;
        
        if (scrollingDown) {
          // Scrolling down - remove sticky class (behave like 'none')
          this.header.classList.remove('shopify-section-header-sticky', 'scrolled-past-header');
          this.header.classList.remove('bottom-border');
          // On index/blog page, keep absolute positioning when scrolling down to prevent content gap
          if (isTransparentHeaderPage && this.headerGroup) {
            this.headerGroup.classList.remove('header-group-sticky-enabled');
            this.headerGroup.style.position = '';
          }
        } else if (scrollingUp) {
          // Scrolling up - add sticky class and scrolled-past-header for color change (white background)
          this.header.classList.add('shopify-section-header-sticky', 'scrolled-past-header');
          if (isTransparentHeaderPage) {
            this.header.classList.add('bottom-border');
          } else {
            this.header.classList.remove('bottom-border');
          }
          // On index/blog page, switch to relative when header is sticky (scrolling up)
          if (isTransparentHeaderPage && this.headerGroup) {
            this.headerGroup.classList.add('header-group-sticky-enabled');
            this.headerGroup.style.position = 'relative';
          }
        }
        // If no movement, keep current state
      }
    } else {
      // For 'always' and 'none', use original behavior
      if (scrollTop > this.headerBounds.bottom) {
        // Remove any hidden state first
        this.header.classList.remove('shopify-section-header-hidden');
        // Force a reflow to ensure removal is applied
        void this.header.offsetHeight;
        
        this.header.classList.add('scrolled-past-header');
        if (isTransparentHeaderPage) {
          this.header.classList.add('bottom-border');
        } else {
          this.header.classList.remove('bottom-border');
        }
        // On index/blog page, switch from absolute to relative when scrolled for sticky to work
        if (isTransparentHeaderPage && this.headerGroup) {
          const mainContent = document.querySelector('main#MainContent');
          if (mainContent) {
            mainContent.style.transition = 'none';
            mainContent.style.marginTop = `calc(-1 * var(--header-height, 70px))`;
          }

          this.headerGroup.style.transition = 'none';
          this.headerGroup.classList.add('header-group-sticky-enabled');
          this.headerGroup.style.position = 'relative';
          void this.headerGroup.offsetHeight;
          requestAnimationFrame(() => {
            this.headerGroup.style.transition = '';
            if (mainContent) {
              mainContent.style.transition = '';
            }
          });
        }
      } else {
        this.header.classList.remove('scrolled-past-header', 'bottom-border');
        // On index/blog page, switch back to absolute when at top
        if (isTransparentHeaderPage && this.headerGroup && this.headerIsAlwaysSticky) {
          const mainContent = document.querySelector('main#MainContent');
          if (mainContent) {
            mainContent.style.transition = 'none';
            mainContent.style.marginTop = '0';
          }
          this.headerGroup.classList.remove('header-group-sticky-enabled');
          this.headerGroup.style.position = '';
          void this.headerGroup.offsetHeight;
          requestAnimationFrame(() => {
            if (mainContent) {
              mainContent.style.transition = '';
            }
          });
        }
      }
    }
    
    // Handle 'always' sticky behavior
    if (currentStickyType !== 'on-scroll-up' && currentStickyType !== 'none') {
      // Handle 'always' sticky behavior
      if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        if (this.preventHide) return;
        requestAnimationFrame(this.hide.bind(this));
      } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        if (!this.preventReveal) {
          requestAnimationFrame(this.reveal.bind(this));
        } else {
          window.clearTimeout(this.isScrolling);

          this.isScrolling = setTimeout(() => {
            this.preventReveal = false;
          }, 66);

          requestAnimationFrame(this.hide.bind(this));
        }
      } else if (scrollTop <= this.headerBounds.top) {
        requestAnimationFrame(this.reset.bind(this));
      }
    }

    this.currentScrollTop = scrollTop;
  }

  hide() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
    if (this.headerGroup) {
      this.headerGroup.classList.add('header-group-sticky-enabled');
    }
    this.closeMenuDisclosure();
    this.closeSearchModal();
  }

  reveal() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.remove('shopify-section-header-hidden');
    void this.header.offsetHeight;
    this.header.classList.add('shopify-section-header-sticky', 'animate');
    if (this.headerGroup) {
      this.headerGroup.classList.add('header-group-sticky-enabled');
    }
  }

  reset() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
    
    const isIndexPage = document.body.classList.contains('index');
    const isBlogPage = document.body.classList.contains('blog');
    const isTransparentHeaderPage = isIndexPage || isBlogPage;
    const isMobile = this.mobileMediaQuery.matches;
    const currentStickyType = isMobile ? this.mobileStickyType : this.desktopStickyType;
    
    if (isTransparentHeaderPage && currentStickyType === 'on-scroll-up' && this.headerGroup) {
      this.headerGroup.classList.remove('header-group-sticky-enabled');
      this.headerGroup.style.position = '';
    }
  }

  closeMenuDisclosure() {
    this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
    this.disclosures.forEach((disclosure) => disclosure.close());
  }

  closeSearchModal() {
    this.searchModal = this.searchModal || this.header.querySelector('details-modal');
    if (this.searchModal) this.searchModal.close(false);
  }
}

// Register the custom element
customElements.define('sticky-header', StickyHeader);

if (window.Shopify && window.Shopify.designMode) {
  const refreshStickyHeaderInEditor = () => {
    requestAnimationFrame(() => {
      const stickyHeader = document.querySelector('sticky-header');
      if (stickyHeader && stickyHeader.header) {
        stickyHeader.desktopStickyType =
          stickyHeader.getAttribute('data-sticky-type-desktop') || stickyHeader.getAttribute('data-sticky-type') || 'none';
        stickyHeader.mobileStickyType = stickyHeader.getAttribute('data-sticky-type-mobile') || 'none';
        stickyHeader.setAnnouncementBarOffset();
        stickyHeader.setHeaderHeight();
        stickyHeader.updateStickyType();
      }
    });
  };

  [
    'shopify:section:load',
    'shopify:section:unload',
    'shopify:section:select',
    'shopify:section:deselect',
    'shopify:section:reorder',
    'shopify:block:select',
    'shopify:block:deselect',
  ].forEach((eventName) => {
    document.addEventListener(eventName, refreshStickyHeaderInEditor);
  });
}
